/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing stream processing gates feature behavior from runtime flags. */
import type { Action } from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema';
import { getDocument } from '@atlaskit/browser-apis';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { Metric } from '@atlassian/browser-metrics/types';
import { isRemixGeneratedFlow } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { getConfluenceResultAcceptanceAttributesForAskQuestion } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/get-confluence-result-acceptance-attributes-for-ask-question';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import { is3pApp } from '@atlassian/conversation-assistant-chat-prompt-input/utils/is-3p-app';
import {
	type FollowUpSource,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	type ExperienceNameType,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type TrackAIEventPayloads } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type Agent,
	type FetchStreamReturn,
	type FileUploadResponseItem,
	isChannelNotFoundError,
	type MessageType,
	type ParsedSSEMessage,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import { type Store as ChatContextStore } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	createProvisionalConversationAgent,
	resolveConversationAgentIdForRouting,
} from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import { deriveAgentSendRequestIdentity } from '@atlassian/conversation-assistant-store/controllers/chat/send-request-agent-identity';
import {
	type AgentMetadata,
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	isStoreAssistant,
	isStoreHuman,
	type StoreAssistant,
	type StoreGuardBlock,
	type StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { messageLatencyMetrics } from '../../common/utils/browser-metrics/messageLatencyMetrics';
import { extractContentFeatures } from '../../common/utils/content-features/extractContentFeatures';
import { recordValidMessage } from '../../common/utils/csat-eligibility-storage/recordValidMessage';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isRovoCustomAgent } from '../../common/utils/is-rovo-custom-agent';
import { DEFAULT_AGENT } from '../agents/constants';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle/StreamLifecycleState';
import { setLastMessageSentAt } from '../last-message-sent/setLastMessageSentAt';

import { buildUnifiedStreamTaskTarget } from './buildUnifiedStreamTaskTarget';
import { clearStatus } from './clearStatus';
import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import { createBroadcastStreamLifecycle } from './createBroadcastStreamLifecycle';
import { DeepResearchError } from './error-classes/DeepResearchError';
import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { findLastAssistantAction } from './findLastAssistantAction';
import { generateId } from './generateId';
import { generateTmpMessageId } from './generateTmpMessageId';
import { getPageContext } from './get-page-context';
import { getViewContext } from './get-view-context';
import { getConversation } from './getConversation';
import { getInteractionModeRequested } from './getInteractionModeRequested';
import { getInvokedFrom } from './getInvokedFrom';
import { getMetricEndHelper } from './getMetricEndHelper';
import { getRequestedModeAttributes } from './getRequestedModeAttributes';
import { getSourceControlValueForBackend } from './getSourceControlValueForBackend';
import { handleStreamTaskStatus } from './handleStreamTaskStatus';
import { interactionModeExecutedAttributes } from './interactionModeExecutedAttributes';
import { isEmptyConversation } from './isEmptyConversation';
import { markStreamingMessagesAsComplete } from './markStreamingMessagesAsComplete';
import { populateSendMessageContext } from './populateSendMessageContext';
import { processStream } from './processStream';
import { applyResultAttributionContext } from './resultAttributionContext';
import type {
	ChatStoreActionsForSendMessage,
	SendMessageParams,
	SendMessageTrackingAttributes,
	StreamActionInfo,
} from './sendMessageActionTypes';
import { createStoreAbortController } from './store-abort-controller';
import type { ChatStoreAction, ChatStoreContainerProps, MessageSource } from './types';

type SendMessageExperienceNameParamsWithTaskMode = {
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	taskModeEnabled?: boolean;
	fastModeEnabled?: boolean;
	isCustomRovoAgent?: boolean;
};

const getSendMessageExperienceName = (
	params: SendMessageExperienceNameParamsWithTaskMode,
): ExperienceNameType => {
	if (params.isCustomRovoAgent) {
		return ExperienceName.SEND_MESSAGE_ROVO_CUSTOM_AGENT;
	}

	if (params.deepResearchEnabled) {
		return ExperienceName.SEND_DR_MESSAGE;
	}

	if (params.thinkDeeperEnabled) {
		return ExperienceName.SEND_TD_MESSAGE;
	}

	if (params.taskModeEnabled) {
		return ExperienceName.SEND_TASK_MODE_MESSAGE;
	}

	return ExperienceName.SEND_MESSAGE;
};

const getInitialTrackingAttributes: (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	taskModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| {
				interactionSource?: InteractionSource;
				followUpSource?: FollowUpSource;
				invokedFrom?: string;
				aiFeatureName?: string;
		  }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	template: {
		templateId?: string;
		templateType?: string;
	},
	forcedContentType: string | undefined,
) => SendMessageTrackingAttributes = (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	taskModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| {
				interactionSource?: InteractionSource;
				followUpSource?: FollowUpSource;
				invokedFrom?: string;
				aiFeatureName?: string;
		  }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	template: { templateId?: string; templateType?: string },
	forcedContentType: string | undefined,
) => {
	return {
		pluginsInvoked: new Set(),
		skillsInvoked: [],
		executedStratusSkill: false,
		hasSources: false,
		messageId: '',
		humanMessageId: '',
		requestId: undefined,
		messageActions: [],
		messageDynamicUIData: [],
		invokedFrom: undefined,
		instrumentationId,
		messageSentTimeStamp: new Date().toISOString(),
		citedProductList: {},
		sourceProducts: {},
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		taskModeEnabled,
		webSearchEnabled,
		interactionSource: messageSources?.interactionSource,
		followUpSource: messageSources?.followUpSource ?? 'none',
		aiFeatureName: messageSources?.aiFeatureName,
		isFirstPrompt,
		promptFeatures: extractContentFeatures(prompt),
		fileCount: files?.length ?? 0,
		appFilters: appFilters ?? [],
		appFilterCount: appFilters?.length ?? 0,
		template,
		deepResearchInvoked: false,
		forcedContentType,
		interactionModeRequested: getInteractionModeRequested({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			taskModeEnabled,
		}),
	};
};

const getOptimisticMessageTypes = (
	deepResearchEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
) => {
	const messageType: MessageType[] = [];

	if (fg('rovo_chat_chat_history_refresh')) {
		if (deepResearchEnabled) {
			messageType.push('DEEP_RESEARCH');
		}
		if (webSearchEnabled) {
			messageType.push('WEB_SEARCH');
		}
	}

	return { messageType };
};

const initLatencyMetrics = ({
	deepResearchEnabled,
	thinkDeeperEnabled,
	fastModeEnabled,
	agentId,
}: {
	deepResearchEnabled: boolean | undefined;
	thinkDeeperEnabled: boolean | undefined;
	fastModeEnabled: boolean | undefined;
	agentId: string | undefined;
}) => {
	const rovoCustomAgent = isRovoCustomAgent(agentId);
	const sendMessageLatencyMetrics = messageLatencyMetrics.createSendMessageMetrics({
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		isRovoCustomAgent: rovoCustomAgent,
	});
	const timeToFirstAnswerPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstAnswerPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToFirstActionPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstActionPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToActionCompletionLatencyMetrics =
		messageLatencyMetrics.createTimeToActionCompletionMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});

	const metricsStartTime = window.performance.now();
	sendMessageLatencyMetrics.start({ startTime: metricsStartTime });
	timeToFirstAnswerPartLatencyMetrics.start({ startTime: metricsStartTime });

	if (fg('aifc_create_enabled') || expValEquals('cc-maui-experiment', 'isEnabled', true)) {
		timeToFirstActionPartLatencyMetrics.start();
		timeToActionCompletionLatencyMetrics.start();
	}

	const stopInteractionMetricsTimeToFirstAnswerPart = getMetricEndHelper(
		timeToFirstAnswerPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToFirstActionPart = getMetricEndHelper(
		timeToFirstActionPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToActionCompletion = getMetricEndHelper(
		timeToActionCompletionLatencyMetrics,
	);

	const cancelStreamingMetrics = () => {
		stopInteractionMetricsTimeToFirstAnswerPart('cancel');
		stopInteractionMetricsTimeToFirstActionPart('cancel');
		stopInteractionMetricsTimeToActionCompletion('cancel');
	};

	return {
		sendMessageLatencyMetrics,
		timeToFirstAnswerPartLatencyMetrics,
		timeToFirstActionPartLatencyMetrics,
		timeToActionCompletionLatencyMetrics,
		stopInteractionMetricsTimeToFirstAnswerPart,
		stopInteractionMetricsTimeToFirstActionPart,
		stopInteractionMetricsTimeToActionCompletion,
		cancelStreamingMetrics,
		metricsStartTime,
	};
};

const hydrateStoreWithNewMessage = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dispatch: <T extends Action<ChatStorePrivateState, any, any>>(actionThunk: T) => ReturnType<T>,
	conversationId: string,
	messageType: MessageType[],
	tmpHumanMessageId: string,
	tmpAssistantMessageId: string,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	sourceAgentIdForTemporaryLocalConversation: string,
	setConversationData: ChatStoreActionsForSendMessage['setConversationData'],
	addHumanMessage: ChatStoreActionsForSendMessage['addHumanMessage'],
	addAssistantMessage: ChatStoreActionsForSendMessage['addAssistantMessage'],
	clearFollowUpMessages: ChatStoreActionsForSendMessage['clearFollowUpMessages'],
	// The per-turn selected/@-mention override that actually routed this send, when
	// one exists (`deriveAgentSendRequestIdentity().routedSelectedAgentId`). This is
	// deliberately NOT `routingAgentId` (which falls back to the conversation agent):
	// the optimistic author must honour the caller's per-send agent intent (e.g. a
	// cross-surface default-Rovo "ask Rovo" into a specialist conversation, which must
	// stay default Rovo) and only switch to a genuine per-turn override here.
	routedSelectedAgentId?: string,
) => {
	const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();

	const updateCurrentConversation = (data: Partial<Conversation>) => {
		dispatch(
			setConversationData({
				conversationId,
				data,
			}),
		);
	};

	// Author the optimistic assistant by per-turn override (`routedSelectedAgentId`),
	// else caller intent (`sourceAgentIdForTemporaryLocalConversation` = explicit/default
	// agent) — never the routing fallback, which would resolve to the conversation agent
	// and mis-author a cross-surface default-Rovo "ask Rovo". See ai-mate AGENTS.md
	// "Rovo Chat agent identity, routing, and display". Gate-off keeps caller intent;
	// on flag cleanup keep the `routedSelectedAgentId ?? sourceAgentId...` form.
	const normalizedRoutedSelectedAgentId = routedSelectedAgentId || undefined;
	const optimisticAssistantAuthorNamedId =
		fg('rovo_chat_agent_selection') && normalizedRoutedSelectedAgentId
			? normalizedRoutedSelectedAgentId
			: sourceAgentIdForTemporaryLocalConversation;

	updateCurrentConversation({
		status: CHAT_STORE_STATUS.awaitingResponse,
		storeAbortController,
		lastMessageType: messageType,
		lastAgentMessageStatus: 'IN_PROGRESS',
	});
	dispatch(
		addHumanMessage({
			id: tmpHumanMessageId,
			content: prompt,
			conversationId,
			fileUploads: files,
		}),
	);
	dispatch(
		addAssistantMessage({
			id: tmpAssistantMessageId,
			content: '',
			conversationId,
			// Authored by the routed agent (see derivation above) so the per-turn
			// "{agent} is thinking" indicator reflects it.
			agentNamedId: optimisticAssistantAuthorNamedId,
			agentAuthorId:
				fg('rovo_chat_agent_selection') && normalizedRoutedSelectedAgentId
					? normalizedRoutedSelectedAgentId
					: undefined,
		}),
	);
	dispatch(clearFollowUpMessages({ conversationId }));

	return { abortController, storeAbortController, confirmAbort, updateCurrentConversation };
};

const handleSendMessageServiceCallFailure = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	error: any,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	streamActionInfo: StreamActionInfo,
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	experienceTracker: ExperienceTrackerAPI,
	sendMessageExperienceNameCompiled: string,
	trackingAttributes: SendMessageTrackingAttributes,
	updateCurrentConversation: (data: Partial<Conversation>) => void,
	sendMessageLatencyMetrics: Metric,
	cancelStreamingMetrics: () => void,
	broadcastStreamLifecycle: (status: StreamLifecycleState) => void,
	confirmAbort: () => void,
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void,
	messageSources: Partial<MessageSource> | undefined,
	abortController: AbortController,
	hasLoggedInitiatedEvent: boolean,
	tmpAssistantMessageId: string,
	clearMessageResearch: (args_0: { messageId: string }) => void,
	getState: () => ChatStorePrivateState,
	conversationId: string,
	storeUserMessages: boolean,
) => {
	// Convert deep research related errors to DeepResearchError when deep research is enabled
	let convertedError = error;
	if (deepResearchEnabled) {
		convertedError = new DeepResearchError(error.message, {
			traceId: error.errorAttributes?.traceId,
			requestId: error.errorAttributes?.requestId,
		});
	}

	// Returns bad request error because agent is deactivated or user has no permission
	const isAgentDeactivated =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED);
	const isNoAgentPermission =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN) &&
		fg('convo-ai-enable-agents-use-permission');

	if (isAgentDeactivated || isNoAgentPermission) {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error,
			attributes: {
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
				},
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		updateCurrentConversation({
			status: CHAT_STORE_STATUS.deactivatedAgentError,
			traces: [],
			errorCode: fg('convo-ai-enable-agents-use-permission')
				? error.errorAttributes.errorCode
				: undefined,
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
		return;
	} else if (error instanceof Error && error.name === 'AbortError') {
		// Check if the abort was programmatic or not. `abortController.signal.aborted` is a readonly field that is only set to true when called programmatically
		// The browser can abort requests to free up resources & save power (esp. on background tabs). When it does this, it **does not** set `abortController.signal.aborted` to true
		const isProgrammaticAbort = abortController.signal.aborted;

		experienceTracker.abort({
			name: sendMessageExperienceNameCompiled,
			reason: 'Aborted',
			attributes: {
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				isProgrammaticAbort,
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
				},
			});
		}

		trackAIMAUEvent({
			type: 'dismissed',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			subjectId: trackingAttributes.interactionSource,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		updateCurrentConversation(
			clearStatus({ lastAgentMessageStatus: isProgrammaticAbort ? 'CANCELLED' : 'FAILED' }),
		);
		clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
		clearMessageResearch({ messageId: tmpAssistantMessageId });
		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

		confirmAbort();

		return;
	} else {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error,
			attributes: {
				deepResearchEnabled,
				fastModeEnabled,
				thinkDeeperEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
				},
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		throw convertedError;
	}
};

/**
 * Compute the per-send routing identity for a send.
 *
 * Wraps `deriveAgentSendRequestIdentity` with the shared inputs (default agent,
 * explicit caller agent id, and the draft-only-on-chatInput override rule) so the
 * outer early-handler fallback and the inner per-send path stay in lockstep.
 *
 * The no-override wire/routing fallback follows the CALLER's agent intent
 * (`conversationAgentForRequest`), not necessarily the stored conversation agent:
 * in-sidebar sends pass the conversation's own agent, a cross-surface pass-over
 * passes default Rovo. This keeps author and wire consistent for the turn and
 * matches gate-off. See ai-mate AGENTS.md "Rovo Chat agent identity, routing, and
 * display".
 */
const deriveSendRoutingIdentity = ({
	conversationAgentForRequest,
	agentIdParam,
	inputSelectedAgentIds,
	messageSources,
	omitConversationDefaultAgentRequestFields,
}: {
	conversationAgentForRequest: { id: string; named_id?: string };
	agentIdParam?: string;
	inputSelectedAgentIds?: string[];
	messageSources?: Partial<MessageSource>;
	omitConversationDefaultAgentRequestFields?: boolean;
}) =>
	deriveAgentSendRequestIdentity({
		conversationAgent: conversationAgentForRequest,
		defaultAgent: DEFAULT_AGENT,
		explicitAgentId: agentIdParam,
		omitConversationDefaultAgentRequestFields,
		inputSelectedAgentIds,
		// The unsent agent-selector draft only routes when the send originates with
		// `messageSources.interactionSource === 'chatInput'`; cross-surface handoffs
		// (e.g. the Confluence inline Rovo input) must not inherit the sidebar's draft.
		allowSelectedAgentOverride: messageSources?.interactionSource === 'chatInput',
	});

/**
 * Build the optimistic `conversation.agent` metadata used when a send creates the
 * conversation. Prefers the already-stored conversation agent, then the caller's
 * rich agent metadata when it matches the resolved conversation agent id, then a
 * provisional placeholder built from that id (the header hydrates the rest).
 */
const buildOptimisticConversationAgent = ({
	agent,
	currentConversation,
	conversationAgentId,
}: {
	agent: Agent;
	currentConversation: Conversation | undefined;
	conversationAgentId: string;
}): AgentMetadata | undefined => {
	const callerAgentMatchesConversation = agent.id === conversationAgentId;
	const callerAgentConversationMetadata: AgentMetadata | undefined = callerAgentMatchesConversation
		? {
				agent_id: agent.id,
				agent_named_id: agent.external_config_reference ?? agent.named_id,
				agent_name: agent.name,
				creator_type: agent.creator_type,
				identity_account_id: agent.identity_account_id ?? undefined,
				is_default: agent.is_default,
				forge_url: agent.icon ?? undefined,
			}
		: undefined;
	return (
		currentConversation?.agent ??
		callerAgentConversationMetadata ??
		createProvisionalConversationAgent(conversationAgentId)
	);
};

export const sendMessageActionProvider: (params: SendMessageParams) => ChatStoreAction =
	({
		prompt,
		/*
As of RAGE-60 the default agent is no longer required to be sent to the backend, however
it is still required for other local logic
TODO: https://product-fabric.atlassian.net/browse/RAGE-418
*/
		agent = DEFAULT_AGENT,
		agentId: agentIdParam,
		conversationId: conversationIdParam,
		storeUserMessages,
		messageSources,
		files,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		taskModeEnabled,
		webSearchEnabled,
		appFilters,
		addCurrentPageAsContextUserPreference,
		productKey,
		source,
		promptStreamHook,
		creationContextParams,
		unsupportedRemixNodeTypes,
		additionalAnalyticAttributes,
		contentContext,
		minionAlias,
		onTempMessageIdReplacedOverride,
		contentId,
		openChat,
		openChatOnError,
		agentVersion,
		spaceId,
		searchArtifact,
		skillSelectionSource,
		skipEditorContext,
		shouldExtractConversationScopes,
		parentMessageId,
		editRollback,
		inputSelectedAgentIds,
		setConversationData,
		createConversationOptimistically,
		addHumanMessage,
		addAssistantMessage,
		clearFollowUpMessages,
		setSelectedConversation,
		reconnectToInProgressStream,
	}) =>
	async (
		{ dispatch, getState, setState },
		{
			assistanceService,
			experienceTracker,
			sendAnalyticsEvent,
			chatContextStoreActions,
			trackAIMAUEvent,
			getMessages,
			setMessages,
			processStreamResearch,
			markIncompleteResearchesErrored,
			updateResearchMessageId,
			clearMessageResearch,
			addImplicitResearchPlan,
			resetHasReceivedAnswerPart,
			getChatInitiator,
			processMessageStreamAction,
			processMessageStreamComplete,
			processMessageStreamError,
			processMessageStreamActionClassification,
			broadcastStreamStatus,
			onNewConversationStartingWithSendMessage,
			onTempMessageIdReplaced: containerOnTempMessageIdReplaced,
			browserContextSanitizer,
			viewDeadAirBroker,
			getIsChatMounted,
			capabilities,
			unifiedTasksActions,
			onSpaceSourceRecommendations,
		},
	) => {
		const initialConversationForRouting = getConversation(
			getState(),
			conversationIdParam ?? getState().currentConversationId,
		);
		const conversationAgentIdForRoutingFallback = resolveConversationAgentIdForRouting({
			conversation: initialConversationForRouting,
			fallbackAgentId: agent.id,
		});
		// `sourceAgentId` for the temporary local conversation; per-turn routing is computed by `deriveSendRoutingIdentity`.
		const sourceAgentIdForTemporaryLocalConversation = agentIdParam ?? agent.id;
		const { messageType } = getOptimisticMessageTypes(deepResearchEnabled, webSearchEnabled);

		setLastMessageSentAt(Date.now());

		let pageContext: string | undefined;

		// Snapshot captured right before the optimistic add when `editRollback` is set,
		// so that a terminal failure can restore the conversation to its pre-edit state
		// by slicing back to this length and re-appending `editRollback.restoreMessages`.
		let rollbackSnapshot: { conversationId: string; lengthBeforeOptimistic: number } | undefined;

		const performEditRollback = () => {
			if (!editRollback || !rollbackSnapshot) {
				return;
			}
			const { conversationId: rollbackConvId, lengthBeforeOptimistic } = rollbackSnapshot;
			const current = getMessages(rollbackConvId);
			setMessages(rollbackConvId, [
				...current.slice(0, lengthBeforeOptimistic),
				...editRollback.restoreMessages,
			]);
		};

		const sendMessageFn = async ({ conversationId }: { conversationId: string }) => {
			const getContentIdForAskQuestionAttributes = (): string | undefined =>
				contentId === undefined ? undefined : String(contentId);

			const getResultAttributesForAskQuestion = () => {
				if (!expValEquals('cc-disambiguation-in-cwr', 'isEnabled', true)) {
					return undefined;
				}

				const confluenceContentId = getContentIdForAskQuestionAttributes();
				if (!confluenceContentId) {
					return undefined;
				}

				return getConfluenceResultAcceptanceAttributesForAskQuestion({
					messages: getMessages(conversationId),
					contentId: confluenceContentId,
				});
			};

			const currentConversation = getConversation(getState(), conversationId);
			const currentConversationAgentId = resolveConversationAgentIdForRouting({
				conversation: currentConversation,
				fallbackAgentId: conversationAgentIdForRoutingFallback,
			});
			const conversationAgentForOptimisticCreation = buildOptimisticConversationAgent({
				agent,
				currentConversation,
				conversationAgentId: currentConversationAgentId,
			});
			const { routedSelectedAgentId, routingAgentId, agentIdForRequest, agentNamedIdForRequest } =
				deriveSendRoutingIdentity({
					conversationAgentForRequest: { id: agent.id, named_id: agent.named_id },
					agentIdParam,
					inputSelectedAgentIds,
					messageSources,
					omitConversationDefaultAgentRequestFields: true,
				});
			const sendMessageExperienceNameCompiled = getSendMessageExperienceName({
				deepResearchEnabled,
				thinkDeeperEnabled,
				taskModeEnabled,
				fastModeEnabled,
				isCustomRovoAgent: isRovoCustomAgent(routingAgentId),
			});
			const resolvedAgentVersion = agentVersion ?? currentConversation?.agentVersion;

			if (currentConversation?.storeAbortController) {
				// Silently aborting if there is another operation in progress
				return;
			}

			const tmpHumanMessageId = generateId();

			const tmpAssistantMessageId = generateTmpMessageId(conversationId);

			let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
			let responseData: FetchStreamReturn<StreamMessage>['responseData'] | undefined;
			let hasLoggedInitiatedEvent = false;

			const isFirstPrompt = isEmptyConversation(conversationId);

			const contentContextFromStore = chatContextStoreActions.getIsStagingArea()
				? 'staging-area'
				: 'global';
			const contentContextValue = contentContext ?? contentContextFromStore;

			let trackingAttributes: SendMessageTrackingAttributes = getInitialTrackingAttributes(
				routingAgentId,
				tmpAssistantMessageId,
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				taskModeEnabled,
				webSearchEnabled,
				messageSources,
				isFirstPrompt,
				prompt,
				files,
				appFilters,
				{
					templateId: creationContextParams?.templateInput?.templateId,
					templateType: creationContextParams?.templateInput?.templateType,
				},
				creationContextParams?.forcedContentType,
			);

			// Prepare fields for the temporary local conversation created before the backend returns one.
			const conversationAgentForTemporaryLocalConversation = fg('rovo_chat_agent_selection')
				? conversationAgentForOptimisticCreation
				: undefined;
			const conversationAgentFieldsForTemporaryLocalConversation =
				conversationAgentForTemporaryLocalConversation
					? { agent: conversationAgentForTemporaryLocalConversation }
					: {};

			// Participant membership is backend-authoritative. We intentionally avoid
			// optimistic participant writes here because matching backend semantics would
			// require duplicating dedupe, edit/fork, branch-pruning, and multi-turn
			// recomputation rules in the frontend.
			if (isFirstPrompt) {
				conversationId = crypto.randomUUID();
				onNewConversationStartingWithSendMessage(conversationId, {
					deepResearchEnabled,
					thinkDeeperEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					...(taskModeEnabled ? { planModeEnabled: taskModeEnabled } : {}),
					webSearchEnabled,
					useCurrentPageContext: addCurrentPageAsContextUserPreference,
					appFilters,
				});
				// Create the conversation with its initial identity; first-turn routing may
				// refine `conversation.agent` below.
				dispatch(
					createConversationOptimistically({
						id: conversationId,
						status: CHAT_STORE_STATUS.awaitingResponse,
						sourceAgentId:
							currentConversation?.sourceAgentId ?? sourceAgentIdForTemporaryLocalConversation,
						lastMessageType: messageType,
						lastAgentMessageStatus: 'IN_PROGRESS',
						resultAttributionContext: currentConversation?.resultAttributionContext,
						// Immediately scope the optimistic conversation to the space so it
						// appears under the space in the sidenav rather than the regular list.
						...(spaceId
							? FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
								? { rovoSpaceId: spaceId }
								: {}
							: {}),
						...(resolvedAgentVersion && fg('rovo_agent_versioning_enabled')
							? { agentVersion: resolvedAgentVersion }
							: {}),
						...conversationAgentFieldsForTemporaryLocalConversation,
					}),
				);
				recordValidMessage();

				// First-turn routing can refine display identity; keep `sourceAgentId` as history.
				const normalizedRoutingAgentId = routingAgentId || undefined;
				const routedConversationAgent =
					createProvisionalConversationAgent(normalizedRoutingAgentId);
				if (
					fg('rovo_chat_agent_selection') &&
					routedConversationAgent &&
					normalizedRoutingAgentId !== currentConversationAgentId
				) {
					dispatch(
						setConversationData({
							conversationId,
							data: { agent: routedConversationAgent },
						}),
					);
				}
			}

			const {
				sendMessageLatencyMetrics,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToActionCompletion,
				cancelStreamingMetrics,
				metricsStartTime,
			} = initLatencyMetrics({
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				agentId: routingAgentId,
			});

			experienceTracker.start({
				name: sendMessageExperienceNameCompiled,
				id: conversationId,
				attributes: {
					conversationId,
					interactionSource: messageSources?.interactionSource,
					featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				},
			});

			const chatInitiator = getChatInitiator();

			const { isRemixFollowUp, lastAssistantMessageDynamicUiType } = (() => {
				const lastAction = findLastAssistantAction(getMessages(conversationId), () => true);
				if (!lastAction || !isRemixGeneratedFlow(lastAction)) {
					return { isRemixFollowUp: false, lastAssistantMessageDynamicUiType: undefined };
				}
				// BE stores dynamicUiType under `contentType` in action data
				const contentType = (lastAction.data as { contentType?: string } | undefined)?.contentType;
				return {
					isRemixFollowUp: true,
					lastAssistantMessageDynamicUiType: contentType,
				};
			})();

			let sendMessageContext: Awaited<ReturnType<typeof populateSendMessageContext>>;
			try {
				sendMessageContext = await populateSendMessageContext(
					chatContextStoreActions,
					isFirstPrompt,
					source,
					prompt,
					addCurrentPageAsContextUserPreference,
					contentContextValue,
					creationContextParams,
					productKey,
					sendAnalyticsEvent,
					contentId,
					lastAssistantMessageDynamicUiType,
					skipEditorContext,
					isRemixFollowUp,
					searchArtifact,
				);
			} catch (error: unknown) {
				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: error as Error,
					attributes: {
						deepResearchEnabled,
						fastModeEnabled,
						thinkDeeperEnabled,
						webSearchEnabled,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						failureSource: 'populateSendMessageContext',
					},
				});

				// Note: We don't track error in error event as discussed here:
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/341408/overview#comment-2004687398
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				return;
			}

			// spaceId comes from the emptyChatLaunch projectContext (e.g. rovo-for-you space card chat button).
			const resolvedSpaceId =
				spaceId ??
				(sendMessageContext.aiFeatureContext?.projectContext as { spaceId?: string } | undefined)
					?.spaceId;

			const storeState = chatContextStoreActions.getAllContext();
			trackingAttributes.contextProvided = Object.entries(storeState).reduce(
				(acc, [key, value]) => {
					acc[key as keyof ChatContextStore] = Boolean(value);
					return acc;
				},
				{} as Record<keyof ChatContextStore, boolean>,
			);
			trackingAttributes.contextProvided.editorContext = Boolean(sendMessageContext.editorContext);
			trackingAttributes.contextProvided.whiteboardContext = Boolean(
				sendMessageContext.whiteboardContext,
			);
			trackingAttributes.contextProvided.slidesContext = Boolean(sendMessageContext.slidesContext);
			trackingAttributes.contextProvided.databaseContext = Boolean(
				sendMessageContext.databaseContext,
			);
			trackingAttributes.contextProvided.browserContext = Boolean(
				sendMessageContext.browserContext,
			);
			trackingAttributes.contextProvided.dashboardContext = Boolean(
				sendMessageContext.dashboardContext,
			);
			trackingAttributes.contextProvided.aiFeatureContext = Boolean(
				sendMessageContext.aiFeatureContext,
			);
			trackingAttributes.contextProvided.jiraWorkflowContext = Boolean(
				sendMessageContext.jiraWorkflowContext,
			);
			trackingAttributes.contextProvided.domContext = Boolean(
				sendMessageContext.browserContext?.context?.htmlBody,
			);

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'sent',
				actionSubject: 'message',
				attributes: {
					timestamp: undefined,
					messageId: trackingAttributes.humanMessageId,
					interactionSource: trackingAttributes.interactionSource,
					followUpSource: trackingAttributes.followUpSource,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					maxModeEnabled: trackingAttributes.taskModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					contextProvided: trackingAttributes.contextProvided,
					initiator: chatInitiator,
					isFirstPrompt: trackingAttributes.isFirstPrompt,
					promptFeatures: trackingAttributes.promptFeatures,
					fileCount: trackingAttributes.fileCount,
					appFilters: trackingAttributes.appFilters,
					appFilterCount: trackingAttributes.appFilterCount,
					has3pApps: trackingAttributes.appFilters?.some((app) => is3pApp(app.friendlyName)),
					template: trackingAttributes.template,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					conversationChannelId: conversationId,
				},
			});

			if (editRollback && !rollbackSnapshot) {
				rollbackSnapshot = {
					conversationId,
					lengthBeforeOptimistic: getMessages(conversationId).length,
				};
			}

			const { abortController, confirmAbort, updateCurrentConversation } =
				hydrateStoreWithNewMessage(
					dispatch,
					conversationId,
					messageType,
					tmpHumanMessageId,
					tmpAssistantMessageId,
					prompt,
					files,
					sourceAgentIdForTemporaryLocalConversation,
					setConversationData,
					addHumanMessage,
					addAssistantMessage,
					clearFollowUpMessages,
					routedSelectedAgentId,
				);

			// Assumed the insert is successful and inserts at the last position
			const answerIndex = getMessages(conversationId).length - 1;

			// Broadcast stream status to content targets for state sharing
			const broadcastStreamLifecycle = createBroadcastStreamLifecycle({
				broadcastStreamStatus,
				chatContextStoreActions,
				contentContextValue,
				targetId: contentId,
				assistantMessageId: tmpAssistantMessageId,
			});

			if (fg('rovo_chat_send_context_ready_event')) {
				broadcastStreamLifecycle(StreamLifecycleState.SEND_CONTEXT_READY);
			}

			// Initialize action tracking
			let streamActionInfo: StreamActionInfo = {
				isAction: false,
			};

			// Create message stream
			try {
				// Derive page context for analytics from the available context objects
				pageContext = getPageContext({
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					databaseContext: sendMessageContext.databaseContext,
					aiFeatureContext: sendMessageContext.aiFeatureContext,
					browserUrl: sendMessageContext.browserContext?.context?.browserUrl,
				});
				trackingAttributes.pageContext = pageContext;

				trackingAttributes.viewContext = getViewContext({
					aiFeatureContext: sendMessageContext.aiFeatureContext,
				});

				trackingAttributes.invokedFrom = getInvokedFrom({
					messageActionKey: streamActionInfo?.actionKey,
				});

				// Check if the Jira refresh experiment is enabled but default is set to `true` to pass all non-Jira products

				// Omit caller source controls only for sends routed by `routedSelectedAgentId`;
				// see `getSourceControlValueForBackend`.
				const webSearchEnabledForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: webSearchEnabled,
				});
				const appFiltersForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: appFilters,
				});
				const agentNamedIdForBackend = fg('rovo_chat_agent_selection')
					? agentNamedIdForRequest
					: undefined;

				const result = await assistanceService.sendMessageStream({
					message: prompt,
					agentId: agentIdForRequest,
					// Gate-off must not introduce `recipient_agent_named_id` on the wire.
					agentNamedId: agentNamedIdForBackend,
					controller: abortController,
					conversationId,
					storeMessage: storeUserMessages,
					citationsEnabled: true,
					fileIds: files?.map((file) => file.id),
					deepResearchEnabled,
					thinkDeeperEnabled,
					planModeEnabled: taskModeEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					webSearchEnabled: webSearchEnabledForBackend,
					appFilters: appFiltersForBackend?.map((app) => app.source),
					browserContext: sendMessageContext.browserContext,
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					slidesContext: sendMessageContext.slidesContext,
					databaseContext: sendMessageContext.databaseContext,
					additionalContext: sendMessageContext.additionalContext,
					aiFeatureContext: sendMessageContext.aiFeatureContext,
					jiraWorkflowContext: sendMessageContext.jiraWorkflowContext,
					dashboardContext: sendMessageContext.dashboardContext,
					dashboardEditorContext: sendMessageContext.dashboardEditorContext,
					useCurrentBrowsingContext: sendMessageContext.useCurrentBrowsingContext,
					browserContextSanitizer,
					optInToDeepResearchV2: true,
					optInThinkDeeperMessages: true,
					optInAnsNow: true,
					optInNewToolTraces: true,
					optInNewTeamworkTraceIcons: fg('rovo_chat_enable_twg_trace_team26'),
					optInSSEStream: true,
					...(resolvedAgentVersion ? { agentVersion: resolvedAgentVersion } : {}),
					...(resolvedSpaceId ? { spaceId: resolvedSpaceId } : {}),
					capabilities,
					shouldExtractConversationScopes,
					parentMessageId,
				});
				generator = result.generator;
				responseData = result.responseData;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (fg('aifc_show_error_ui_for_inline_rovo')) {
					promptStreamHook?.onError?.(error, tmpAssistantMessageId, {
						openChatOnError,
						conversationId,
					});
				}
				performEditRollback();
				handleSendMessageServiceCallFailure(
					error,
					deepResearchEnabled,
					thinkDeeperEnabled,
					fastModeEnabled,
					webSearchEnabled,
					streamActionInfo,
					chatContextStoreActions,
					experienceTracker,
					sendMessageExperienceNameCompiled,
					trackingAttributes,
					updateCurrentConversation,
					sendMessageLatencyMetrics,
					cancelStreamingMetrics,
					broadcastStreamLifecycle,
					confirmAbort,
					trackAIMAUEvent,
					messageSources,
					abortController,
					hasLoggedInitiatedEvent,
					tmpAssistantMessageId,
					clearMessageResearch,
					getState,
					conversationId,
					storeUserMessages,
				);
				return;
			}

			const {
				hasReceivedFinalResponse,
				hasLoggedInitiatedEvent: hasLoggedInitiatedEventForStream,
				hasAbortedForImplicitThinkDeeper,
				implicitThinkDeeperSendMessageMetrics,
				implicitThinkDeeperExperienceName,
				guardEventReceived: wasGuardTerminated,
			} = await processStream({
				prompt,
				generator,
				productKey,
				answerIndex,
				tmpHumanMessageId,
				tmpAssistantMessageId,
				thinkDeeperEnabled,
				fastModeEnabled,
				deepResearchEnabled,
				webSearchEnabled,
				sendMessageExperienceNameCompiled,
				traceId: responseData?.traceId,
				updateCurrentConversation,
				trackingAttributes,
				conversationId,
				getState,
				promptStreamHook,
				openChat,
				openChatOnError,
				experienceTracker,
				setMessages,
				getMessages,
				processStreamResearch,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamError,
				processMessageStreamActionClassification,
				onSpaceSourceRecommendations,
				containerOnTempMessageIdReplaced,
				updateResearchMessageId,
				sendAnalyticsEvent,
				chatContextStoreActions,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToActionCompletion,
				broadcastStreamLifecycle,
				sendMessageLatencyMetrics,
				cancelStreamingMetrics,
				onTempMessageIdReplacedOverride,
				streamActionInfo,
				trackAIMAUEvent,
				messageSources,
				additionalAnalyticAttributes,
				unsupportedRemixNodeTypes,
				hasLoggedInitiatedEvent,
				clearMessageResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				viewDeadAirBroker,
				creationContextParams,
				minionAlias: assistanceService.getConfig().minionAlias,
				agentId: routingAgentId,
				agentName: agent?.name,
				agentIdentityAccountId: agent?.identity_account_id,
				storeUserMessages,
				skillSelectionSource,
				assistanceService,
				abortController,
				getIsChatMounted,
				metricsStartTime,
				onTaskStatusUpdate: (streamMessage) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					handleStreamTaskStatus({
						conversationId,
						taskMessage: streamMessage.message,
						messageId: trackingAttributes.messageId,
						target: buildUnifiedStreamTaskTarget(unifiedTasksActions),
					});
				},
				onTodoSnapshot: ({ todos }) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					unifiedTasksActions.setTaskTodoSnapshot({
						conversationId,
						todoSnapshot: { todos },
					});
				},
				onPromptBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
				onPromptRedacted: (streamMessage) => {
					// Only update the human message with the redacted prompt.
					// Keep the assistant placeholder — the stream continues with the actual response.
					const chat = getMessages(conversationId);
					let lastHumanIdx = -1;
					chat.forEach((m, i) => {
						if (isStoreHuman(m)) {
							lastHumanIdx = i;
						}
					});
					if (lastHumanIdx === -1) {
						return;
					}
					const newChat = [...chat];
					newChat[lastHumanIdx] = {
						...(chat[lastHumanIdx] as StoreHuman),
						redactedPrompt: streamMessage.message.redacted_prompt,
					};
					setMessages(conversationId, newChat);
				},
				onResponseRedacted: (streamMessage) => {
					// Set redactedResponse on the latest assistant message so the scribble UI renders.
					// Do NOT set guardEventReceived — the stream continues after response redaction.
					const chat = getMessages(conversationId);
					let lastAssistantIdx = -1;
					chat.forEach((m, i) => {
						if (isStoreAssistant(m)) {
							lastAssistantIdx = i;
						}
					});
					if (lastAssistantIdx === -1) {
						return;
					}
					const newChat = [...chat];
					newChat[lastAssistantIdx] = {
						...(chat[lastAssistantIdx] as StoreAssistant),
						redactedResponse: streamMessage.message.redacted_response,
					};
					setMessages(conversationId, newChat);
				},
				onResponseBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
				onAttachmentBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
			});

			hasLoggedInitiatedEvent = hasLoggedInitiatedEventForStream;

			// TODO: This should be behind the !aborted logic, but due to a bug in the BE service, aborted requests
			// will client connection with the server yet still complete in the background. This results in the UI showing
			// no message updates, until reload when the actual resolved message can be sesen.
			// Once that is fixed, we can place this behind the !aborted logic.
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'received',
				actionSubject: 'agentMessage',
				attributes: {
					// messageId must remain the ID received from the server in the FINAL_RESPONSE
					// in order to map correctly to our event funnel, e.g `agentMessage received` -> `agentResponse content copied`
					messageId: trackingAttributes.messageId,
					humanMessageId: trackingAttributes.humanMessageId,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					requestId: trackingAttributes.requestId,
					hasSources: trackingAttributes.hasSources,
					citedProductList: trackingAttributes.citedProductList,
					connectorInfo: trackingAttributes.connectorInfo,
					sourceProducts: trackingAttributes.sourceProducts,
					has3pApps: Object.keys(trackingAttributes.sourceProducts ?? {}).some(is3pApp),
					timestamp: new Date().toISOString(),
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					initiator: chatInitiator,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					scenarioId: trackingAttributes.scenarioId,
					// Lumina content descriptors (see trackingAttributes.hasLumina).
					// Joined on messageId against the
					// `luminaComponent shown` render event to detect Lumina returned
					// by the backend but never rendered.
					...(trackingAttributes.hasLumina
						? {
								hasLumina: true,
								luminaType: trackingAttributes.luminaType,
								luminaCount: trackingAttributes.luminaCount,
							}
						: {}),
				},
			});

			const wasProgrammaticallyAborted = abortController.signal.aborted;
			updateCurrentConversation(
				clearStatus({
					lastAgentMessageStatus: wasProgrammaticallyAborted ? 'CANCELLED' : 'SUCCESSFUL',
				}),
			);

			setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));

			if (abortController.signal.aborted) {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
						viewContext: trackingAttributes.viewContext,
						attributes: {
							...getRequestedModeAttributes(trackingAttributes),
						},
					});
				}

				trackAIMAUEvent({
					type: 'dismissed',
					userGenerated: true,
					messageId: trackingAttributes.messageId,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					subjectId: trackingAttributes.interactionSource,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					attributes: {
						...getRequestedModeAttributes(trackingAttributes),
						...(messageSources?.entryPoint && { entryPoint: messageSources.entryPoint }),
					},
				});

				confirmAbort();
				broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

				// Call the cancel API if we have a message ID
				if (trackingAttributes.messageId) {
					assistanceService
						.cancelMessageStream({
							conversationId,
							messageId: trackingAttributes.messageId,
						})
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						.catch((error: any) => {
							// Log but don't throw - the abort is already happening
							logException({
								name: 'rovoCancelMessageStream',
								exception: error,
								tags: {
									conversationId,
									messageId: trackingAttributes.messageId,
								},
							});
						});
				}

				// For implicit think-deeper, the auto-mode experience was already aborted inside processStream;
				// abort the re-attributed think-deeper experience instead.
				experienceTracker.abort({
					name: hasAbortedForImplicitThinkDeeper
						? (implicitThinkDeeperExperienceName ?? ExperienceName.SEND_TD_MESSAGE)
						: sendMessageExperienceNameCompiled,
					reason: 'Aborted',
					attributes: {
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isProgrammaticAbort: true, // always true because we're within abortController.signal.aborted (further explained in similar abort for AbortError case)
					},
				});
				experienceTracker.abort({
					name: ExperienceName.AIFC_MESSAGE,
					reason: 'stream aborted',
				});
				// Always mark incomplete researches as errored on abort — the backend can
				// autonomously trigger think-deeper even in auto mode
				markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
				// Clear research data to remove lingering think deeper state
				clearMessageResearch({ messageId: tmpAssistantMessageId });
				return;
			}

			// Only fire if the request wasn't aborted
			else {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
						viewContext: trackingAttributes.viewContext,
						attributes: {
							...getRequestedModeAttributes(trackingAttributes),
						},
					});
				}

				//If we're done processing the stream and no answer part has been processed, then it means that the BE is hanging. Throw an error
				if (!hasReceivedFinalResponse && !wasGuardTerminated) {
					const agentDetails = await assistanceService.getAgentDetails(routingAgentId);
					if (agentDetails.deactivated) {
						// If agent is deactivated, throw correct error
						const agentError = new NoFinalResponseError(
							'The selected agent may no longer be available.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: agentError,
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
								...getRequestedModeAttributes(trackingAttributes),
							},
						});

						updateCurrentConversation({
							status: CHAT_STORE_STATUS.deactivatedAgentError,
							traces: [],
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
						return;
					} else {
						// Agent is active, BE hanging
						const timeoutError = new NoFinalResponseError(
							'The response took too long and timed out. Try refreshing the page.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: timeoutError,
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
								...getRequestedModeAttributes(trackingAttributes),
							},
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

						throw timeoutError;
					}
				}

				/**
				 * IMPORTANT: CRITICAL ANALYTICS EVENT
				 *
				 * This event is essential for business metrics and monitoring.
				 *
				 * DO NOT MODIFY the structure, content, or emission logic of this event without:
				 *   - Careful review of potential impacts on data quality, privacy, and business reporting.
				 *   - Explicit approval from both analytics and security stakeholders.
				 *
				 * Reference: Incident HOT-120175 (UGC page title and URLs leak)
				 * Event Registry: https://data-portal.internal.atlassian.com/analytics/registry/68936?tabIndex=Details
				 */
				const dynamicUiType =
					creationContextParams?.dynamicUiType ?? sendMessageContext?.editorContext?.dynamicUiType;
				const conversationForResultAttribution =
					getConversation(getState(), conversationId) ?? currentConversation;
				const resultAttributionAnalytics = applyResultAttributionContext({
					resultAttributionContext: conversationForResultAttribution?.resultAttributionContext,
					routingAgentId,
					messageSources,
					additionalAnalyticAttributes,
				});
				if (resultAttributionAnalytics.shouldClearResultAttributionContext) {
					dispatch(
						setConversationData({
							conversationId,
							data: { resultAttributionContext: undefined },
						}),
					);
				}
				trackAIMAUEvent({
					type: 'viewed',
					subjectId: resultAttributionAnalytics.messageSources?.followUpSource,
					messageId: trackingAttributes.messageId,
					source: resultAttributionAnalytics.messageSources?.interactionSource,
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					invokedFrom: trackingAttributes.invokedFrom,
					proactive: false,
					userGenerated: true,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					timestamp: new Date().toISOString(),
					citedProductList: trackingAttributes.citedProductList,
					connectorInfo: trackingAttributes.connectorInfo,
					sourceProducts: trackingAttributes.sourceProducts,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					skillsInvoked: trackingAttributes.skillsInvoked,
					executedStratusSkill: trackingAttributes.executedStratusSkill,
					responseSources: trackingAttributes.sources,
					usesJsm: trackingAttributes.usesJsm,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					conversationChannelId: conversationId,
					scenarioId: trackingAttributes.scenarioId,
					pageContext: trackingAttributes.pageContext,
					attributes: {
						...interactionModeExecutedAttributes(trackingAttributes.interactionModeExecuted),
						...(dynamicUiType && { dynamicUiType }),
						...(creationContextParams?.dynamicUiSubtype && {
							dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
						}),
						isTabVisible: getDocument()?.visibilityState === 'visible',
						...(resultAttributionAnalytics.additionalAnalyticAttributes &&
							resultAttributionAnalytics.additionalAnalyticAttributes),
						...(getResultAttributesForAskQuestion() ?? {}),
						...(creationContextParams?.dynamicUiSource && {
							remixNodeTypes: trackingAttributes.remixNodeTypes,
							...(trackingAttributes.unsupportedRemixNodeTypes !== undefined && {
								unsupportedRemixNodeTypes: trackingAttributes.unsupportedRemixNodeTypes,
							}),
							...(trackingAttributes.remixWordCount !== undefined && {
								remixWordCount: trackingAttributes.remixWordCount,
							}),
						}),
						...(trackingAttributes.aiFeatureName && {
							aiFeatureName: trackingAttributes.aiFeatureName,
						}),
						...(resultAttributionAnalytics.messageSources?.entryPoint && {
							entryPoint: resultAttributionAnalytics.messageSources.entryPoint,
						}),
					},
				});

				// Experience is aborted in `abortRequests`
				// For implicit think-deeper, succeed the re-attributed think-deeper reliability SLO instead
				experienceTracker.succeed({
					name: hasAbortedForImplicitThinkDeeper
						? (implicitThinkDeeperExperienceName ?? ExperienceName.SEND_TD_MESSAGE)
						: sendMessageExperienceNameCompiled,
				});

				// Broadcast that the stream has completed successfully
				broadcastStreamLifecycle(StreamLifecycleState.COMPLETED);

				if (fg('rovo_chat_chat_history_refresh')) {
					//If user is viewing a different conversation or not viewing ConversationAssitant, optimistically set conversation to unread
					if (getState().currentConversationId !== conversationId) {
						dispatch(
							setConversationData({
								conversationId,
								data: { readStatus: false },
							}),
						);
					}
				}
			}

			// For implicit think-deeper, stop the re-attributed think-deeper metric instead
			if (!hasAbortedForImplicitThinkDeeper) {
				sendMessageLatencyMetrics.stop({
					customData: messageSources?.interactionSource
						? { interactionSource: messageSources.interactionSource }
						: undefined,
				});
			} else {
				implicitThinkDeeperSendMessageMetrics?.stop({
					customData: messageSources?.interactionSource
						? { interactionSource: messageSources.interactionSource }
						: undefined,
				});
			}
		};

		const conversationId = conversationIdParam ?? getState().currentConversationId;

		try {
			await sendMessageFn({ conversationId });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (isChannelNotFoundError(error)) {
				const createdChannel = await assistanceService.createConversationChannel();

				setState({
					conversations: {
						...getState().conversations,
						[createdChannel.id]: {
							id: createdChannel.id,
							name: createdChannel.name ?? generateConversationName(),
							timeCreated: createdChannel.time_created,
							status: CHAT_STORE_STATUS.idle,
							traces: [],
							messages: [],
							...(createdChannel.agent && fg('rovo_chat_chat_history_refresh')
								? { agent: createdChannel.agent }
								: {}),
						},
					},
				});

				setMessages(createdChannel.id, []);

				dispatch(
					setSelectedConversation({
						conversationId: createdChannel.id,
					}),
				);

				await sendMessageFn({ conversationId: createdChannel.id });
			} else {
				performEditRollback();
				throw error;
			}
		}
	};
